// Shared telemetry vocabulary. None of these properties grant product access.
export const TEST_RUN_COOKIE = "pdfparse_test_run";
export const AUDIENCE_COOKIE = "__Host-pdfparse_analytics_audience";

export type TrafficProperties = Record<
  string,
  string | number | boolean | null
>;

export function readCookie(cookies: string, name: string): string | null {
  const entry = cookies
    .split(";")
    .find((part) => part.trim().startsWith(`${name}=`));
  if (!entry) return null;
  try {
    return decodeURIComponent(entry.trim().slice(name.length + 1));
  } catch {
    return null;
  }
}

export function validTestRunId(value: string | null): string | null {
  return value && /^[a-zA-Z0-9_-]{1,80}$/.test(value) ? value : null;
}

export function audienceProperties(
  account: "staff" | "test" | "external" | "unknown",
  testRunId: string | null,
): TrafficProperties {
  const test = account === "test" || testRunId !== null;
  return {
    traffic_audience: test ? "test" : account,
    is_internal_traffic:
      test || account === "staff" ? true : account === "unknown" ? null : false,
    is_staff: account === "staff",
    is_test: test,
    test_run_id: testRunId,
    traffic_marker_source: testRunId
      ? "browser_test"
      : account === "staff" || account === "test"
        ? "account_allowlist"
        : account === "unknown"
          ? "unavailable"
          : "none",
  };
}

export function accountAudience(
  userId: string | undefined,
  staffUserIds = "",
  testUserIds = "",
  user?: {
    email?: string;
    emailVerified?: boolean;
    isAnonymous?: boolean | null;
  },
): "staff" | "test" | "external" {
  const includes = (values: string) =>
    values
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .includes(userId ?? "");
  if (userId && includes(testUserIds)) return "test";
  if (userId && includes(staffUserIds)) return "staff";
  const domain = user?.email?.trim().toLowerCase().split("@")[1];
  if (
    userId &&
    user?.isAnonymous !== true &&
    user?.emailVerified === true &&
    domain &&
    (domain === "pdfparse.net" || domain.endsWith(".pdfparse.net"))
  )
    return "staff";
  return "external";
}

function object(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

export function cloudflareProperties(
  cf?: Record<string, unknown>,
): TrafficProperties {
  const bot = object(cf?.botManagement);
  const score =
    typeof bot.score === "number" &&
    Number.isInteger(bot.score) &&
    bot.score >= 1 &&
    bot.score <= 99
      ? bot.score
      : null;
  const category =
    typeof cf?.verifiedBotCategory === "string" &&
    cf.verifiedBotCategory.length > 0
      ? cf.verifiedBotCategory.slice(0, 100)
      : null;
  const verified =
    typeof bot.verifiedBot === "boolean" ? bot.verifiedBot : null;
  const signedAgent =
    typeof bot.signedAgent === "boolean" ? bot.signedAgent : null;
  const js = object(bot.jsDetection);
  return {
    cf_signal_source: cf ? "cloudflare_request_cf" : "unavailable",
    cf_verified_bot: verified,
    cf_verified_bot_category: category,
    cf_signed_agent: signedAgent,
    cf_bot_score: score,
    cf_bot_score_available: score !== null,
    cf_js_detection_passed: typeof js.passed === "boolean" ? js.passed : null,
    cf_bot_classification:
      verified === true || category !== null || signedAgent === true
        ? "verified_automation"
        : score !== null && score < 30
          ? "likely_automation"
          : "unclassified",
    cf_country: typeof cf?.country === "string" ? cf.country : null,
    cf_asn: typeof cf?.asn === "number" ? cf.asn : null,
    cf_as_organization:
      typeof cf?.asOrganization === "string"
        ? cf.asOrganization.slice(0, 200)
        : null,
  };
}
